import { useFormContext } from "react-hook-form";
import { useState, useEffect, useRef } from "react";
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from "@shadcn/components/ui/form";
import { Label } from "@shadcn/components/ui/label";
import { Input } from "@shadcn/components/ui/input";
import { Calendar } from "@shadcn/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@shadcn/components/ui/popover";
import { getValidationConfig } from "@forms/form-render/helpers/getValidationConfig";
import { getFieldHelperText } from "@forms/form-render/helpers/getFieldHelperText";
import { DatePickerProps } from "./DatePicker.types";
import InputMask from "react-input-mask";
import { ControllerRenderProps } from "react-hook-form";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@shadcn/lib/utils";
import { Button } from "@shadcn/components/ui/button";
import { useIsMobile } from "@shadcn/hooks/use-is-mobile";
import { useEnquiryEvaluationTimeZoneId } from "@forms/form-render/contexts/EnquiryEvaluationTimeZoneContext";
import { getCalendarTodayIsoInTimeZone } from "@schema-types/enquiryEvaluationTimeZone";
import { normalizeDateConfigValue } from "@forms/form-render/helpers/normalizeDateConfigValue";

const MASK = "99/99/9999";

function dateToYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function calculateAgeFromBirthYmd(birthYmd: string, ianaTz: string): number {
  const [by, bm, bd] = birthYmd.split("-").map((x) => parseInt(x, 10));
  const todayIso = getCalendarTodayIsoInTimeZone(ianaTz);
  const [ty, tm, td] = todayIso.split("-").map((x) => parseInt(x, 10));
  let age = ty - by;
  if (tm < bm || (tm === bm && td < bd)) {
    age--;
  }
  return age;
}

function convertToMaskFormat(dateString: string | undefined, isUSFormat: boolean): string {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return isUSFormat ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
  } catch {
    return "";
  }
}

function convertFromMaskFormat(maskValue: string, isUSFormat: boolean): string | undefined {
  const cleaned = maskValue?.replace(/_/g, "").replace(/\s/g, "").trim() ?? "";
  if (!cleaned || cleaned.length !== 10) return undefined;
  try {
    const parts = cleaned.split("/");
    if (parts.length !== 3) return undefined;
    const [first, second, fullYear] = parts.map(Number);

    let month: number, day: number;
    if (isUSFormat) {
      month = first;
      day = second;
    } else {
      day = first;
      month = second;
    }

    if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;
    if (fullYear < 100 || fullYear > 9999) return undefined;
    const date = new Date(fullYear, month - 1, day);
    if (isNaN(date.getTime())) return undefined;
    if (date.getMonth() !== month - 1 || date.getDate() !== day || date.getFullYear() !== fullYear) return undefined;
    return `${fullYear}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  } catch {
    return undefined;
  }
}

export const DatePicker = ({ label, name, required, validation, config, disabled }: DatePickerProps) => {
  const { control } = useFormContext();
  const evaluationTz = useEnquiryEvaluationTimeZoneId();
  const validationConfig = getValidationConfig(validation);
  const dateFormat = config.dateFormat || "GB";
  const isUSFormat = dateFormat === "US";
  const helperTextFormatted = getFieldHelperText(config.helperText, required);
  const isMobile = useIsMobile();

  const VALID_DATE_REGEX = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

  const validateDate = (value: string | Date | undefined): string | true => {
    if (!value) {
      return true;
    }

    const strValue = typeof value === "string" ? value : undefined;
    if (strValue && !VALID_DATE_REGEX.test(strValue)) {
      return "Enter a date in the format " + (isUSFormat ? "mm/dd/yyyy" : "dd/mm/yyyy");
    }

    const dateValue = typeof value === "string" ? new Date(value) : value;
    if (isNaN(dateValue.getTime())) {
      return true;
    }

    const todayIso = getCalendarTodayIsoInTimeZone(evaluationTz);
    const valueYmd =
      typeof value === "string" && VALID_DATE_REGEX.test(value) ? value : dateToYmd(dateValue);

    if (config.minDate !== undefined && config.minDate !== null) {
      const minDate = new Date(config.minDate);
      if (!isNaN(minDate.getTime())) {
        const minYmd = dateToYmd(minDate);
        if (valueYmd < minYmd) {
          return "The date must be after " + minDate.toLocaleDateString();
        }
      }
    }

    if (config.maxDate !== undefined && config.maxDate !== null) {
      const maxDate = new Date(config.maxDate);
      if (!isNaN(maxDate.getTime())) {
        const maxYmd = dateToYmd(maxDate);
        if (valueYmd > maxYmd) {
          return "The date must be before " + maxDate.toLocaleDateString();
        }
      }
    }

    if (config.mustBeInFuture) {
      if (valueYmd <= todayIso) {
        return "The date must be in the future";
      }
    }

    if (config.mustBeInPast) {
      if (valueYmd >= todayIso) {
        return "The date must be in the past";
      }
    }

    if (config.minAge && VALID_DATE_REGEX.test(valueYmd)) {
      const age = calculateAgeFromBirthYmd(valueYmd, evaluationTz);
      if (age < config.minAge) {
        return `The person must be at least ${config.minAge} years old`;
      }
    }

    if (config.maxAge && VALID_DATE_REGEX.test(valueYmd)) {
      const age = calculateAgeFromBirthYmd(valueYmd, evaluationTz);
      if (age > config.maxAge) {
        return `The person must be no more than ${config.maxAge} years old`;
      }
    }

    return true;
  };

  const placeholder = isUSFormat ? "MM/DD/YYYY" : "DD/MM/YYYY";
  const normalizedDefaultValue = normalizeDateConfigValue(config.defaultValue);

  return (
    <FormField
      control={control}
      name={name}
      defaultValue={normalizedDefaultValue}
      rules={{
        ...validationConfig,
        required: {
          value: required,
          message: "This is a required field",
        },
        validate: validateDate,
      }}
      render={({ field }) => (
        <DatePickerUnified
          field={field}
          label={label}
          name={name}
          disabled={disabled}
          helperText={helperTextFormatted}
          placeholder={config.placeholder || placeholder}
          minDate={config.minDate}
          maxDate={config.maxDate}
          isUSFormat={isUSFormat}
          isMobile={isMobile}
        />
      )}
    />
  );
};

interface DatePickerUnifiedProps {
  field: ControllerRenderProps<any, string>;
  label?: string;
  name: string;
  disabled?: boolean;
  helperText?: string;
  placeholder: string;
  minDate?: Date;
  maxDate?: Date;
  isUSFormat: boolean;
  isMobile: boolean;
}

const DatePickerUnified = ({
  field,
  label,
  name,
  disabled,
  helperText,
  placeholder,
  minDate,
  maxDate,
  isUSFormat,
  isMobile,
}: DatePickerUnifiedProps) => {
  const rawValue =
    typeof field.value === "string"
      ? field.value
      : field.value instanceof Date
        ? field.value.toISOString().split("T")[0]
        : undefined;

  const isValidStoredFormat = (v: string) => /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(v);

  const initialMaskValue =
    rawValue && isValidStoredFormat(rawValue)
      ? convertToMaskFormat(rawValue, isUSFormat)
      : (typeof rawValue === "string" ? rawValue : "") || "";
  const [maskValue, setMaskValue] = useState(initialMaskValue);
  const [pickerOpen, setPickerOpen] = useState(false);
  const skipNextClearRef = useRef(false);
  const [displayMonth, setDisplayMonth] = useState<Date>(() => {
    const d = rawValue ? new Date(rawValue) : new Date();
    return isNaN(d.getTime()) ? new Date() : d;
  });

  useEffect(() => {
    if (rawValue) {
      const newMaskValue = isValidStoredFormat(rawValue)
        ? convertToMaskFormat(rawValue, isUSFormat)
        : rawValue;
      setMaskValue(newMaskValue);
    } else if (skipNextClearRef.current) {
      skipNextClearRef.current = false;
    } else {
      setMaskValue("");
    }
  }, [rawValue, isUSFormat]);

  useEffect(() => {
    if (pickerOpen && rawValue && isValidStoredFormat(rawValue)) {
      const d = new Date(rawValue);
      if (!isNaN(d.getTime())) setDisplayMonth(d);
    }
  }, [pickerOpen, rawValue]);

  const selectedDate =
    typeof field.value === "string" && field.value && isValidStoredFormat(field.value)
      ? new Date(field.value)
      : field.value instanceof Date
        ? field.value
        : undefined;

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      const dateOnlyString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      field.onChange(dateOnlyString);
      setMaskValue(convertToMaskFormat(dateOnlyString, isUSFormat));
      setPickerOpen(false);
    } else {
      field.onChange(undefined);
      setMaskValue("");
      setPickerOpen(false);
    }
  };

  const inputContent = (
    <InputMask
      mask={MASK}
      value={maskValue}
      onChange={(e) => {
        const inputValue = e.target.value;
        setMaskValue(inputValue);
        const converted = convertFromMaskFormat(inputValue, isUSFormat);
        if (converted !== undefined) field.onChange(converted);
      }}
      onBlur={(e) => {
        const inputValue = e.target.value;
        const converted = convertFromMaskFormat(inputValue, isUSFormat);
        if (converted !== undefined) {
          field.onChange(converted);
        } else {
          skipNextClearRef.current = true;
          field.onChange(inputValue.trim() ? inputValue : undefined);
        }
        field.onBlur();
      }}
      disabled={disabled}
      maskChar={null}
    >
      {(inputProps: any) => (
        <Input
          {...inputProps}
          value={maskValue}
          id={name}
          type="text"
          placeholder={placeholder}
          disabled={disabled}
          aria-label={label}
          inputMode={isMobile ? "numeric" : "text"}
          pattern={isMobile ? "[0-9]*" : undefined}
          className={cn("pl-9", inputProps.className)}
        />
      )}
    </InputMask>
  );

  return (
    <FormItem className="flex flex-col space-y-2">
      {label && <Label htmlFor={name}>{label}</Label>}
      <FormMessage />
      <FormControl>
        <div className="relative">
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <div className="relative">
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute left-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:bg-muted hover:text-foreground"
                  disabled={disabled}
                  aria-label="Open date picker"
                >
                  <CalendarIcon className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              {inputContent}
            </div>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                month={displayMonth}
                onMonthChange={setDisplayMonth}
                onSelect={handleDateSelect}
                disabled={
                  disabled
                    ? true
                    : minDate ?? maxDate
                      ? [
                          ...(minDate ? [{ before: minDate }] : []),
                          ...(maxDate ? [{ after: maxDate }] : []),
                        ]
                      : undefined
                }
                captionLayout="dropdown"
              />
            </PopoverContent>
          </Popover>
        </div>
      </FormControl>
      {helperText && <FormDescription>{helperText}</FormDescription>}
    </FormItem>
  );
};
